// IP Sale pricing engine: prices/types come from data-* attributes (no fetches),
// and all DOM writes are batched into one requestAnimationFrame per run.

const EXCLUDED_VARIANT_IDS = new Set([47393331740837, 42388327399589]);
const EXCLUDED_TYPES = new Set(['combo', 'flashsale', 'flashstore', 'merch', 'free']);
const LIP_TYPES = new Set(['lip balm', 'lip care', 'lip']);
const CHEAP_PRICE_FLOOR = 348;

// Bundle/pair prices from the Liquid JSON block (#ip-sale-config / #ip-sale-page-config).
// Falls back to hardcoded defaults when missing or unparsable.
function ipReadPriceConfig() {
  const el = document.getElementById('ip-sale-page-config') || document.getElementById('ip-sale-config');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (_) {
    return null;
  }
}
const IP_PRICE_CONFIG = ipReadPriceConfig() || {};

const MIN_ELIGIBLE_TOTAL = (IP_PRICE_CONFIG.tier_2 || 69900) / 100;
const FACE_WASH_PAIR_FLAT = IP_PRICE_CONFIG.facewash_pair || 49900;
const LIP_PAIR_FLAT = IP_PRICE_CONFIG.lip_pair || 35000;
// Bundle direction: true = leftover unit priced high (cheap units bundled);
// default = leftover priced low. Mirrors ip_use_highest_first in Liquid.
const USE_HIGHEST_FIRST = IP_PRICE_CONFIG.use_highest_first === true;
// Face Wash is identified by product type (like LIP_TYPES), not only data-face-wash,
// since that flag stays false when compare_at_price is nil even if MRP > ₹249.
const FACE_WASH_TYPES = new Set(['face wash', 'facewash', 'face-wash']);
const FACE_WASH_MRP_MIN = 249;
// Offer caps: pair/tier pricing applies to the first N units only, the rest is overflow.
// Liquid clamps/snaps the values; 8 is the fallback.
const ipReadCap = (value, fallback) => {
  const cap = Number(value);
  return Number.isInteger(cap) && cap >= 0 ? cap : fallback;
};
const LIP_CAP = ipReadCap(IP_PRICE_CONFIG.lip_cap, 8);
const FACE_WASH_CAP = ipReadCap(IP_PRICE_CONFIG.facewash_cap, 8);

const TIER_FLAT = {
  2: IP_PRICE_CONFIG.tier_2 || 69900,
  3: IP_PRICE_CONFIG.tier_3 || 104900,
  4: IP_PRICE_CONFIG.tier_4 || 134900,
  6: IP_PRICE_CONFIG.tier_6 || 209800,
  8: IP_PRICE_CONFIG.tier_8 || 269800,
  9: IP_PRICE_CONFIG.tier_9 || 299700,
};
const TIER_CAP = [2, 3, 4, 6, 8].includes(Number(IP_PRICE_CONFIG.tier_cap)) ? Number(IP_PRICE_CONFIG.tier_cap) : 8;

const debounce = (fn, ms) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};

const formatRupees = (paise) => `₹${Math.round(paise / 100).toLocaleString('en-IN')}`;

// Pairs units of the same cart line first, then leftovers across lines.
// Total is unchanged; a line with both units shows the exact flat price (e.g. ×2 = ₹350).
const pairUnitsByLine = (units) => {
  const byLine = new Map();
  for (const unit of units) {
    if (!byLine.has(unit.key)) byLine.set(unit.key, []);
    byLine.get(unit.key).push(unit);
  }
  const pairs = [];
  const leftovers = [];
  for (const lineUnits of byLine.values()) {
    for (let i = 0; i + 1 < lineUnits.length; i += 2) pairs.push([lineUnits[i], lineUnits[i + 1]]);
    if (lineUnits.length % 2 === 1) leftovers.push(lineUnits[lineUnits.length - 1]);
  }
  for (let i = 0; i + 1 < leftovers.length; i += 2) pairs.push([leftovers[i], leftovers[i + 1]]);
  return pairs;
};

// Adds each pair's flat price to discountMap (paise, by line key). u1 is rounded,
// u2 gets the remainder, so the pair always sums to exactly pairFlat.
const applyPairFlat = (discountMap, pairs, pairFlat) => {
  for (const [u1, u2] of pairs) {
    const totalWeight = u1.price + u2.price;
    if (totalWeight <= 0) continue;
    const share1 = Math.round((u1.price / totalWeight) * (pairFlat / 100)) * 100;
    const share2 = pairFlat - share1;
    discountMap.set(u1.key, (discountMap.get(u1.key) || 0) + share1);
    discountMap.set(u2.key, (discountMap.get(u2.key) || 0) + share2);
  }
};

const runPricingEngine = (config) => {
  const priceEls = [...document.querySelectorAll(config.priceSelector)];
  if (priceEls.length === 0) return;

  const totalCartQty = priceEls.reduce((sum, el) => sum + Number(el.dataset.quantity || 1), 0);
  if (totalCartQty <= 1) return;

  // All data comes from DOM attributes set by Liquid — zero network requests
  const items = priceEls.map((el) => {
    const compareAtPrice = Number(el.dataset.originalPrice || 0);
    const price = Number(el.dataset.price || 0);
    const productType = el.dataset.productType || '';
    const faceWashMrp = compareAtPrice > price ? compareAtPrice : price;
    return {
      el,
      key: el.dataset.key,
      variantId: Number(el.dataset.variantId),
      productId: el.dataset.productId,
      quantity: Number(el.dataset.quantity || 1),
      compareAtPrice,
      price,
      productType,
      hasVd: el.dataset.hasVd === 'true',
      // Tagged 'exclude-product' in Shopify — must never get Lip/Face-Wash/tier
      // pricing, same treatment as a hardcoded EXCLUDED_VARIANT_IDS unit.
      excluded: el.dataset.excluded === 'true',
      // Bucket membership: any face-wash-typed item, regardless of price —
      // it must never fall through into the general tier bucket. Pool
      // eligibility (the ₹499-pair pool) is a separate, narrower flag below.
      faceWash: FACE_WASH_TYPES.has(productType),
      // Only MRP > ₹249 is eligible for the ₹499-pair pool; MRP <= ₹249
      // stays in the face-wash bucket but is always priced at its own price
      // (mirrors cart-mode-ip-sale.liquid / cart.liquid).
      faceWashPremium: FACE_WASH_TYPES.has(productType) && faceWashMrp > FACE_WASH_MRP_MIN,
      finalEl: el.querySelector('.cart-item__final-price'),
      strikeEl: el.querySelector('s'),
    };
  });

  const hasExpensiveItem = items.some(
    (item) =>
      !EXCLUDED_VARIANT_IDS.has(item.variantId) &&
      !item.excluded &&
      !EXCLUDED_TYPES.has(item.productType) &&
      !LIP_TYPES.has(item.productType) &&
      !item.faceWash &&
      item.price > CHEAP_PRICE_FLOOR,
  );

  const tierUnits = [];
  const lipUnits = [];
  const faceWashUnits = [];

  for (const item of items) {
    if (EXCLUDED_VARIANT_IDS.has(item.variantId)) continue;
    if (item.excluded) continue;
    if (EXCLUDED_TYPES.has(item.productType)) continue;

    if (LIP_TYPES.has(item.productType)) {
      for (let i = 0; i < item.quantity; i++) lipUnits.push(item);
      continue;
    }

    if (item.faceWash) {
      for (let i = 0; i < item.quantity; i++) faceWashUnits.push(item);
      continue;
    }

    if (item.price <= CHEAP_PRICE_FLOOR && !hasExpensiveItem) continue;

    for (let i = 0; i < item.quantity; i++) tierUnits.push(item);
  }

  // Collect all DOM writes; flush in one rAF at the end
  const domWrites = new Map();
  const strikesToHide = [];
  const strikesToShow = [];

  // Full-cart accumulators for the cart-page price breakdown
  let totalCartOrigPaise = 0;
  let totalCartFinalPaise = 0;

  const eligibleTotal = tierUnits.reduce((sum, u) => sum + u.price, 0);

  if (eligibleTotal < MIN_ELIGIBLE_TOTAL && lipUnits.length < 2 && faceWashUnits.length < 2) {
    for (const item of items) {
      if (item.finalEl) domWrites.set(item.finalEl, formatRupees(item.price * item.quantity * 100));
    }
    requestAnimationFrame(() => {
      for (const [el, text] of domWrites) el.textContent = text;
    });
    return;
  }

  // Lip pair pricing across ALL lip units cart-wide: the cheapest LIP_CAP units
  // are paired, pricier units past the cap are overflow. Mirrors the Liquid.
  const lipDiscountMap = new Map();
  // Equal-price tie-break: larger-quantity line first so its units pair together.
  // Total-neutral, so the Liquid footer total is unaffected.
  const sortedLip = [...lipUnits].sort((a, b) => a.price - b.price || b.quantity - a.quantity);
  const lipCapped = sortedLip.slice(0, LIP_CAP);
  const lipOverflow = sortedLip.slice(LIP_CAP);
  // Units not in a ₹350 pair (odd unit in the cap + all overflow) are charged
  // their own discounted sale price (data-price), not MRP.
  let lipPairSet = lipCapped;
  if (lipCapped.length % 2 === 1) {
    const oddUnit = lipCapped[lipCapped.length - 1];
    lipDiscountMap.set(oddUnit.key, (lipDiscountMap.get(oddUnit.key) || 0) + oddUnit.price * 100);
    lipPairSet = lipCapped.slice(0, -1);
  }
  applyPairFlat(lipDiscountMap, pairUnitsByLine(lipPairSet), LIP_PAIR_FLAT);
  if (lipOverflow.length > 0) {
    for (const unit of lipOverflow) {
      lipDiscountMap.set(unit.key, (lipDiscountMap.get(unit.key) || 0) + unit.price * 100);
    }
  }

  // Face Wash: the ₹499-pair pool is the cheapest premium (MRP > ₹249) units;
  // pricier overflow and cheap units (MRP <= ₹249) are charged their own price.
  const faceWashPremiumUnits = faceWashUnits.filter((u) => u.faceWashPremium);
  const faceWashCheapUnits = faceWashUnits.filter((u) => !u.faceWashPremium);
  // Same equal-price tie-break as the lip pool (total-neutral).
  const sortedFaceWash = [...faceWashPremiumUnits].sort((a, b) => a.price - b.price || b.quantity - a.quantity);
  const faceWashPool = sortedFaceWash.slice(0, FACE_WASH_CAP);
  const faceWashOverflow = sortedFaceWash.slice(FACE_WASH_CAP);
  const faceWashDiscountMap = new Map();
  // Odd unit in the pool (the priciest one) is charged its own price;
  // the rest pair up.
  let faceWashPairSet = faceWashPool;
  if (faceWashPool.length % 2 === 1) {
    const oddUnit = faceWashPool[faceWashPool.length - 1];
    faceWashDiscountMap.set(oddUnit.key, (faceWashDiscountMap.get(oddUnit.key) || 0) + oddUnit.price * 100);
    faceWashPairSet = faceWashPool.slice(0, -1);
  }
  applyPairFlat(faceWashDiscountMap, pairUnitsByLine(faceWashPairSet), FACE_WASH_PAIR_FLAT);
  // Overflow: each unit at its own price.
  for (const unit of faceWashOverflow) {
    faceWashDiscountMap.set(unit.key, (faceWashDiscountMap.get(unit.key) || 0) + unit.price * 100);
  }
  // Cheap face wash (MRP <= ₹249): never pooled, always its own price.
  for (const unit of faceWashCheapUnits) {
    faceWashDiscountMap.set(unit.key, (faceWashDiscountMap.get(unit.key) || 0) + unit.price * 100);
  }

  // Tier pricing: sort direction decides which units are bundled. Highest = cheap
  // units bundled (ascending); default Lowest = expensive units bundled (descending).
  if (USE_HIGHEST_FIRST) {
    tierUnits.sort((a, b) => a.price - b.price);
  } else {
    tierUnits.sort((a, b) => b.price - a.price);
  }

  const eligibleCount = tierUnits.length;
  let tierSlice = tierUnits;
  let overflow = [];
  let tierFlat = null;

  // First TIER_CAP units get that size's flat price; the rest stay at their own price.
  if (eligibleCount > TIER_CAP) {
    tierSlice = tierUnits.slice(0, TIER_CAP);
    overflow = tierUnits.slice(TIER_CAP);
    tierFlat = TIER_FLAT[TIER_CAP];
  } else if (eligibleCount === 5) {
    tierSlice = tierUnits.slice(0, 4);
    overflow = tierUnits.slice(4);
    tierFlat = TIER_FLAT[4];
  } else if (eligibleCount === 7) {
    tierSlice = tierUnits.slice(0, 6);
    overflow = tierUnits.slice(6);
    tierFlat = TIER_FLAT[6];
  } else if (TIER_FLAT[eligibleCount] !== undefined) {
    tierFlat = TIER_FLAT[eligibleCount];
  }

  // Use min(bundled slice total, tier flat) — never inflate prices.
  // Mirrors cart-ip-sale-total.liquid.
  const tierSliceTotal = tierSlice.reduce((sum, u) => sum + u.price, 0);
  if (tierFlat !== null && tierSliceTotal * 100 < tierFlat) {
    tierFlat = null;
  }

  const tierDiscountMap = new Map();
  if (tierFlat !== null) {
    const totalSelling = tierSlice.reduce((sum, u) => sum + u.price, 0);
    if (totalSelling > 0) {
      // Largest-remainder rounding: round each share to the rupee, then hand out
      // the leftover so per-item prices always sum to exactly tierFlat.
      let sharesRoundedTotal = 0;
      const shareEntries = tierSlice.map((unit) => {
        const weight = unit.price / totalSelling;
        const exactShare = tierFlat * weight;
        const roundedShare = Math.round(exactShare / 100) * 100;
        sharesRoundedTotal += roundedShare;
        return { unit, remainder: exactShare - roundedShare, roundedShare };
      });

      const leftoverPaise = tierFlat - sharesRoundedTotal;
      const stepPaise = leftoverPaise >= 0 ? 100 : -100;
      let remainingSteps = Math.round(Math.abs(leftoverPaise) / 100);
      const adjustmentOrder = [...shareEntries].sort((a, b) =>
        leftoverPaise >= 0 ? b.remainder - a.remainder : a.remainder - b.remainder
      );
      for (const entry of adjustmentOrder) {
        if (remainingSteps <= 0) break;
        entry.roundedShare += stepPaise;
        remainingSteps -= 1;
      }

      for (const entry of shareEntries) {
        tierDiscountMap.set(entry.unit.key, (tierDiscountMap.get(entry.unit.key) || 0) + entry.roundedShare);
      }
    }
    for (const unit of overflow) {
      tierDiscountMap.set(unit.key, (tierDiscountMap.get(unit.key) || 0) + unit.price * 100);
    }
  }

  // Build DOM write map — no DOM mutations yet
  let totalOriginalPaise = 0;
  let totalFinalPaise = 0;

  for (const item of items) {
    if (!item.finalEl) continue;

    totalCartOrigPaise += item.price * item.quantity * 100;

    if (EXCLUDED_VARIANT_IDS.has(item.variantId) || item.excluded) {
      if (item.strikeEl) strikesToHide.push(item.strikeEl);
      const excAmt = item.price * item.quantity * 100;
      domWrites.set(item.finalEl, formatRupees(excAmt));
      totalCartFinalPaise += excAmt;
      continue;
    }

    const lipDiscounted = lipDiscountMap.get(item.key);
    const faceWashDiscounted = faceWashDiscountMap.get(item.key);
    const tierDiscounted = tierDiscountMap.get(item.key);

    if (lipDiscounted !== undefined) {
      domWrites.set(item.finalEl, formatRupees(lipDiscounted));
      totalOriginalPaise += (item.compareAtPrice || item.price) * item.quantity * 100;
      totalFinalPaise += lipDiscounted;
      totalCartFinalPaise += lipDiscounted;
      if (item.strikeEl) strikesToShow.push(item.strikeEl);
    } else if (faceWashDiscounted !== undefined) {
      domWrites.set(item.finalEl, formatRupees(faceWashDiscounted));
      totalOriginalPaise += (item.compareAtPrice || item.price) * item.quantity * 100;
      totalFinalPaise += faceWashDiscounted;
      totalCartFinalPaise += faceWashDiscounted;
      if (item.strikeEl) strikesToShow.push(item.strikeEl);
    } else if (tierDiscounted !== undefined) {
      domWrites.set(item.finalEl, formatRupees(tierDiscounted));
      totalOriginalPaise += (item.compareAtPrice || item.price) * item.quantity * 100;
      totalFinalPaise += tierDiscounted;
      totalCartFinalPaise += tierDiscounted;
      if (item.strikeEl) strikesToShow.push(item.strikeEl);
    } else if (item.hasVd) {
      // Variant-level discount only — not touched by lip/tier math, but still
      // contributes its own "You Save" amount and keeps its strikethrough visible.
      const vdAmt = item.price * item.quantity * 100;
      domWrites.set(item.finalEl, formatRupees(vdAmt));
      totalOriginalPaise += (item.compareAtPrice || item.price) * item.quantity * 100;
      totalFinalPaise += vdAmt;
      totalCartFinalPaise += vdAmt;
      if (item.strikeEl) strikesToShow.push(item.strikeEl);
    } else {
      // Cheap item or overflow — show selling price, no strikethrough
      const cheapAmt = item.price * item.quantity * 100;
      domWrites.set(item.finalEl, formatRupees(cheapAmt));
      totalCartFinalPaise += cheapAmt;
    }
  }

  const savingsEl = config.savingsSelector ? document.querySelector(config.savingsSelector) : null;
  const orderTotalEl = config.orderTotalSelector ? document.querySelector(config.orderTotalSelector) : null;
  const grandTotalEl = config.grandTotalSelector ? document.querySelector(config.grandTotalSelector) : null;
  const savingsRowEl = config.savingsRowSelector ? document.querySelector(config.savingsRowSelector) : null;
  const savingsAmountEl = config.savingsAmountSelector ? document.querySelector(config.savingsAmountSelector) : null;

  const freebieBoxEl = config.freebieSavingsSelector ? document.querySelector(config.freebieSavingsSelector) : null;
  const freebieSavingsPaise = freebieBoxEl ? Number(freebieBoxEl.dataset.freebieSavingsPaise || 0) : 0;

  const saveAmount = totalOriginalPaise - totalFinalPaise + freebieSavingsPaise;

  // Single rAF flush — all DOM writes in one frame
  requestAnimationFrame(() => {
    for (const [el, text] of domWrites) el.textContent = text;
    for (const el of strikesToHide) el.hidden = true;
    for (const el of strikesToShow) el.classList.add('is-visible');
    if (savingsEl) {
      if (saveAmount > 0) {
        savingsEl.textContent = `You Save ${formatRupees(saveAmount)}`;
        savingsEl.classList.remove('hidden');
      } else {
        savingsEl.textContent = '';
        savingsEl.classList.add('hidden');
      }
    }
    if (orderTotalEl && totalCartOrigPaise > 0) {
      orderTotalEl.textContent = `₹${Math.round(totalCartOrigPaise / 100).toLocaleString('en-IN')}`;
    }
    if (grandTotalEl && totalCartFinalPaise > 0) {
      grandTotalEl.textContent = `₹${Math.round(totalCartFinalPaise / 100).toLocaleString('en-IN')}`;
    }
    if (savingsRowEl && savingsAmountEl && saveAmount > 0) {
      savingsAmountEl.textContent = `-₹${Math.round(saveAmount / 100).toLocaleString('en-IN')}`;
      savingsRowEl.classList.add('is-visible');
    }
  });
};

const IPSaleCart = {
  init(config) {
    const run = debounce(() => runPricingEngine(config), 150);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }

    document.addEventListener('shopify:section:load', run);
    document.addEventListener('cart:rendered', run);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, run);
      subscribe(PUB_SUB_EVENTS.cartAdd, run);
    }
  },
};

IPSaleCart.init({
  containerSelector: '.cart-drawer-unique-dr',
  priceSelector: '.cart-drawer-unique-dr .cart-item__discounted-prices',
  savingsSelector: '.cart-drawer-unique-dr [data-ip-savings]',
  freebieSavingsSelector: '.cart-drawer-unique-dr [data-ip-freebies]',
});

if (document.querySelector('.custom-cart-page')) {
  IPSaleCart.init({
    containerSelector: '.custom-cart-page',
    priceSelector: '.custom-cart-page .cart-item__discounted-prices',
    savingsSelector: '.custom-cart-page [data-ip-savings]',
    orderTotalSelector: '.custom-cart-page [data-cart-page-order-total]',
    grandTotalSelector: '.custom-cart-page [data-cart-page-grand-total]',
    savingsRowSelector: '.custom-cart-page [data-cart-page-savings-row]',
    savingsAmountSelector: '.custom-cart-page [data-cart-page-savings]',
    freebieSavingsSelector: '.custom-cart-page [data-ip-freebies]',
  });
}

// ── Bundle total for Customers Also Bought: pure calc, mirrors cart-ip-sale-total.liquid
// (no freebie/combo surcharges). items: [{ variantId, price (paise), productType, excluded? }]
// → returns discounted total in paise.
(function () {
  function ipSaleCalcBundleTotal(items) {
    if (!items || !items.length) return 0;

    var lipQty = 0, lipTotal = 0;
    var eligibleQty = 0, eligibleTotal = 0;
    var excludedTotal = 0;

    items.forEach(function (item) {
      var type = String(item.productType || '').trim().toLowerCase();
      var varId = Number(item.variantId || 0);
      var price = Number(item.price || 0);

      if (item.excluded || EXCLUDED_VARIANT_IDS.has(varId) || EXCLUDED_TYPES.has(type)) {
        excludedTotal += price;
        return;
      }
      if (LIP_TYPES.has(type)) { lipQty++; lipTotal += price; }
      else { eligibleQty++; eligibleTotal += price; }
    });

    // Lip pair pricing: ₹350 (35000 paise) per pair, merchant-editable via
    // LIP_PAIR_FLAT; odd lip at avg unit price
    var lipPairs = Math.floor(lipQty / 2);
    var lipOddUnit = (lipQty % 2 === 1 && lipQty > 0) ? Math.round(lipTotal / lipQty) : 0;
    var finalLipTotal = lipPairs * LIP_PAIR_FLAT + lipOddUnit;

    // Tier pricing (mirrors cart-ip-sale-total.liquid)
    var tierPricePaise = eligibleTotal; // default: no tier for 0 or 1 item
    if (eligibleQty >= 10) {
      var extra = eligibleQty - 9;
      var unitAvg = eligibleQty > 0 ? Math.round(eligibleTotal / eligibleQty) : 0;
      tierPricePaise = TIER_FLAT[9] + extra * unitAvg;
    } else if (eligibleQty === 7) {
      tierPricePaise = TIER_FLAT[6] + Math.round(eligibleTotal / 7);
    } else if (TIER_FLAT[eligibleQty] !== undefined) {
      tierPricePaise = TIER_FLAT[eligibleQty];
    }

    // Never inflate — if original total is cheaper than the tier, keep it
    return finalLipTotal + Math.min(eligibleTotal, tierPricePaise) + excludedTotal;
  }

  window.DnkPricing = { calcBundleTotal: ipSaleCalcBundleTotal };
})();
